import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, LayoutDashboard, CalendarDays, FileText, Repeat, GraduationCap,
  ArrowRight, Download, CheckCircle2, Lock, Server, Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Walkthrough } from "@/components/Walkthrough";

const FEATURES = [
  { icon: ShieldCheck, title: "Role-Based Access", desc: "System Admin, RCSM, CNAT, HR and CTM each see exactly what they need — scoped by region and assignment." },
  { icon: LayoutDashboard, title: "Live Dashboards", desc: "Role-aware KPIs, overdue items, training completion and recent activity — every card clickable." },
  { icon: CalendarDays, title: "Calendar & ROTA", desc: "Interactive month-grid rota with blockable slots and full visibility of scheduled work and field visits." },
  { icon: FileText, title: "Editable Templates", desc: "A Microsoft Forms-style builder for reviews, assessments, sign-offs and field visits." },
  { icon: Repeat, title: "Task Automation", desc: "Annual reviews, assessments and 6–8 week reviews are generated automatically as they fall due." },
  { icon: GraduationCap, title: "Training & Compliance", desc: "A company-wide training matrix of skills, eLearning and certificate expiry, with a red/amber/green compliance view by region." },
];

const STATS = [
  { k: "8", l: "Clinical roles supported" },
  { k: "100%", l: "Role-scoped data access" },
  { k: "24/7", l: "Automated task generation" },
  { k: "1", l: "Secure hub for everything" },
];

const stagger = { animate: { transition: { staggerChildren: 0.08 } } };
const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const goApp = () => navigate(user ? "/dashboard" : "/login");

  return (
    <div className="min-h-screen bg-[#0F172A] text-white" data-testid="landing-page">
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0F172A]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 lg:px-8">
          <div className="flex items-center gap-3" data-testid="landing-brand">
            <img src="/logo.png" alt="ESP26" className="h-10 w-10 rounded-xl object-cover" />
            <div className="leading-tight">
              <p className="font-heading text-lg font-extrabold tracking-tight">ESP26</p>
              <p className="text-[11px] text-slate-400">People. Skills. Progress.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/ESP26-Pitch-Deck.pptx"
              download
              data-testid="landing-download-deck-top"
              className="hidden items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-slate-200 transition-colors hover:bg-white/10 sm:flex"
            >
              <Download className="h-4 w-4" /> Pitch deck
            </a>
            <Button
              onClick={goApp}
              data-testid="landing-login-button"
              className="rounded-full bg-orange-500 px-5 font-semibold hover:bg-orange-600"
            >
              {user ? "Go to dashboard" : "Login"}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -right-40 top-0 h-[36rem] w-[36rem] rounded-full bg-orange-500/15 blur-3xl" />
        <div className="pointer-events-none absolute -left-40 bottom-0 h-96 w-96 rounded-full bg-sky-500/10 blur-3xl" />
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-16 lg:grid-cols-2 lg:gap-8 lg:px-8 lg:py-24">
          <motion.div variants={stagger} initial="initial" animate="animate">
            <motion.span
              variants={fadeUp}
              className="inline-flex items-center gap-2 rounded-full border border-orange-500/30 bg-orange-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-orange-400"
            >
              <ShieldCheck className="h-3.5 w-3.5" /> Clinical Workforce Management
            </motion.span>
            <motion.h1
              variants={fadeUp}
              className="mt-5 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
            >
              The central operational hub for{" "}
              <span className="text-orange-500">clinical teams.</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-6 max-w-xl text-base text-slate-300 sm:text-lg">
              ESP26 unifies employee records, regions, training, work allocation and development into
              one secure, role-aware platform — so every manager and clinician sees exactly what matters.
            </motion.p>
            <motion.div variants={fadeUp} className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                onClick={goApp}
                data-testid="landing-hero-cta"
                className="group rounded-full bg-orange-500 px-6 py-6 text-base font-semibold hover:bg-orange-600"
              >
                {user ? "Open dashboard" : "Login to ESP26"}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <a
                href="/ESP26-Pitch-Deck.pptx"
                download
                data-testid="landing-download-deck-hero"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3.5 text-base font-semibold text-slate-100 transition-colors hover:bg-white/10"
              >
                <Download className="h-4 w-4" /> Download pitch deck
              </a>
            </motion.div>
            <motion.div variants={fadeUp} className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-400">
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-orange-500" /> Secure role-based access</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-orange-500" /> Automated reviews</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-orange-500" /> Audit-ready</span>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="relative"
          >
            <div className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1691139601099-932c01ec198b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxjbGluaWNhbCUyMG51cnNpbmclMjB0ZWFtJTIwaGVhbHRoY2FyZSUyMHByb2Zlc3Npb25hbHMlMjBjb2xsYWJvcmF0aW9ufGVufDB8fHx8MTc5MDI4Nzc5MXww&ixlib=rb-4.1.0&q=85"
                alt="Clinical team collaborating"
                className="h-[26rem] w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0F172A]/90 px-5 py-4 shadow-xl backdrop-blur">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-500/15 text-orange-400">
                <Users className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-bold">Whole-region visibility</p>
                <p className="text-xs text-slate-400">RCSMs, CNATs & training in one view</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-white/10 bg-white/[0.03]">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-6 px-5 py-10 lg:grid-cols-4 lg:px-8">
          {STATS.map((s) => (
            <div key={s.l} data-testid={`landing-stat-${s.l.split(" ")[0].toLowerCase()}`}>
              <p className="font-heading text-4xl font-extrabold text-orange-500">{s.k}</p>
              <p className="mt-1 text-sm text-slate-400">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Product walkthrough */}
      <Walkthrough />

      {/* Features */}
      <section className="bg-[#0F172A]" data-testid="landing-features">
        <div className="mx-auto max-w-6xl px-5 py-20 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-orange-400">What ESP26 does</p>            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
              Everything the clinical operation needs, in one place.
            </h2>
          </div>
          <motion.div
            variants={stagger}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, amount: 0.2 }}
            className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
          >
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  variants={fadeUp}
                  data-testid={`landing-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-all hover:-translate-y-1 hover:border-orange-500/40 hover:bg-white/[0.06]"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-500/15 text-orange-400 transition-colors group-hover:bg-orange-500 group-hover:text-white">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-bold">{f.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{f.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Security band */}
      <section className="border-y border-white/10 bg-white/[0.03]">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-16 lg:grid-cols-2 lg:px-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-orange-400">Built to trust</p>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
              Secure, scalable and audit-ready.
            </h2>
            <div className="mt-8 space-y-5">
              {[
                { icon: Lock, t: "Role & region scoped", d: "JWT-secured sessions with authorisation enforced on every endpoint and document." },
                { icon: Server, t: "Cloud-native architecture", d: "React, FastAPI and MongoDB with platform-managed automation jobs." },
                { icon: ShieldCheck, t: "Governance & GDPR", d: "Data export, erasure and consent tracking, an audit register, daily manager digests and a weekly compliance summary." },
              ].map((r) => {
                const Icon = r.icon;
                return (
                  <div key={r.t} className="flex gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-orange-500/15 text-orange-400">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-semibold">{r.t}</p>
                      <p className="mt-0.5 text-sm text-slate-400">{r.d}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1758691461888-b74515208d7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBtZWRpY2FsJTIwb2ZmaWNlJTIwZGFzaGJvYXJkJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3OTAyODc3OTF8MA&ixlib=rb-4.1.0&q=85"
              alt="Clinician working with the platform"
              className="h-full max-h-[24rem] w-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0F172A]">
        <div className="mx-auto max-w-4xl px-5 py-20 text-center lg:px-8">
          <img src="/logo.png" alt="ESP26" className="mx-auto h-14 w-14 rounded-2xl object-cover" />
          <h2 className="mt-6 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
            Bring your clinical workforce together.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-slate-300">
            People. Skills. Progress. — all in one secure place.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button
              onClick={goApp}
              data-testid="landing-cta-login"
              className="group rounded-full bg-orange-500 px-7 py-6 text-base font-semibold hover:bg-orange-600"
            >
              {user ? "Open dashboard" : "Login to ESP26"}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
            <a
              href="/ESP26-Pitch-Deck.pptx"
              download
              data-testid="landing-cta-download"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 text-base font-semibold text-slate-100 transition-colors hover:bg-white/10"
            >
              <Download className="h-4 w-4" /> Download pitch deck
            </a>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-5 text-sm text-slate-500 sm:flex-row lg:px-8">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="ESP26" className="h-7 w-7 rounded-lg object-cover" />
            <span className="font-semibold text-slate-300">ESP26</span>
          </div>
          <p>© {new Date().getFullYear()} ESP26 — Clinical Workforce Management Platform.</p>
          <Link to="/privacy" data-testid="footer-privacy-link" className="font-medium text-slate-400 transition-colors hover:text-orange-400">
            Privacy &amp; Cookie Policy
          </Link>
        </div>
      </footer>
    </div>
  );
}
